<template>
  <div class="collection">
    <div class="container">
      <slot />
    </div>

    <div :class="[{ '--scroll-x': hasOverflowX }, 'collection__content']">
      <div
        :class="[
          { '--video-fullscreen': isVideoFullscreen },
          'collection__items'
        ]"
      >
        <div
          class="collection__items-inner"
          :style="{
            padding: collectionItemsPadding,
            'grid-template-columns': `repeat(${itemColumns ||
              'auto-fit'}, 1fr)`,
            'grid-template-rows': `repeat(${itemRows}, min-content)`,
            'grid-auto-flow': flow,
            width: gridWidth,
            maxWidth: gridMaxWidth
          }"
        >
          <template v-if="fetchedItems.length > 0">
            <div
              v-for="item in fetchedItems"
              :key="itemKey(item)"
              class="collection__items-cell"
            >
              <collection-item
                :page-id="
                  item.type === 'page' ? item.data.id : item.data.page_id
                "
                :title="item.data.title"
                :type="item.type"
                :content-type="item.content_type"
                :image="item.data.banner"
                :video="item.data.video"
                :product="item.data.product"
                :to="item.data.path"
                :card-style="cardStyle"
                :card-display="cardDisplay"
                :show-price="showPrice"
                :show-time="showTime"
                :show-booked="showBooked"
                :item-data="item.data"
                :modifiers="modifiers"
                :image-size="cardImageSize"
                :invert-colors="invertColors"
                :unlock="unlock"
              />
            </div>
          </template>
          <template
            v-else-if="isLoading || !(isDynamic && !isBuilderContext())"
          >
            <div
              v-for="index in placeHolderNumber"
              :key="index"
              class="collection__items-cell --placeholder"
            >
              <collection-item
                :is-loading="isLoading"
                :card-style="cardStyle"
                :card-display="cardDisplay"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <template
      v-if="
        !isBuilderContext() &&
          isDynamic &&
          collection.dynamic_offset === 0 &&
          !isLoading &&
          fetchedItems.length === 0
      "
    >
      <div class="text-center pb-5">
        <span class="text-muted">
          <template>
            No {{ pluralize(dynamicContentType, 2) }}
            found
          </template>
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex"

import { pluralize, isBuilderContext } from "@/lib/utils"
import enumValidator from "@/lib/validators/enum-validator"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { withRatio, toPadding } from "@/lib/stream-utils"
import stringEnumValidator from "@/lib/validators/string-enum-validator"

import CollectionItem from "@/components/CollectionItem"

const HORIZONTAL = "horizontal"
const VERTICAL = "vertical"
export const HORIZONTAL_MAX_WIDTH = 320

export default {
  components: {
    CollectionItem
  },

  props: {
    collection: {
      type: Object,
      default: () => ({})
    },
    direction: {
      type: String,
      default: VERTICAL,
      validator: enumValidator([HORIZONTAL, VERTICAL])
    },
    cardDisplay: {
      type: String,
      default: VERTICAL,
      validator: enumValidator([HORIZONTAL, VERTICAL])
    },
    cardStyle: {
      type: String,
      default: "raised",
      validator: enumValidator(["flat", "raised"])
    },
    cardImageSize: {
      type: Number,
      default: 100
    },
    columns: {
      type: Number,
      default: null
    },
    rows: {
      type: Number,
      default: null
    },
    columnWidthRatio: {
      type: Number,
      default: 100
    },
    spaceXRatio: {
      type: Number,
      default: 100
    },
    spaceTopRatio: {
      type: Number,
      default: 100
    },
    spaceBottomRatio: {
      type: Number,
      default: 100
    },
    modifiers: {
      type: String,
      default: "",
      validator: stringEnumValidator(["date-card", "no-image"])
    },
    items: { type: Array, default: () => [] },
    unlock: { type: Boolean, default: null },
    showPrice: { type: Boolean, default: true },
    showTime: { type: Boolean, default: true },
    showBooked: { type: Boolean, default: false },
    invertColors: { type: Boolean, default: false }
  },

  data() {
    return {
      isLoading: false,
      dynamicItems: [],
      debounce: null,

      HORIZONTAL
    }
  },

  mounted() {
    if (this.isDynamic && this.dynamicContentType) {
      this.fetchDynamicContent()

      // TODO: Remove when access is handled client side
      // This needs to be re-fetched because access is returned from
      // the server with the dynamic content
      EventBus.$on(EVENTS.CHECKOUT_COMPLETED, this.fetchDynamicContent)
    }
  },

  beforeDestroy() {
    EventBus.$off(EVENTS.CHECKOUT_COMPLETED, this.fetchDynamicContent)
  },

  watch: {
    isAuthenticated() {
      // TODO: Remove when access is handled client side
      // This needs to be re-fetched because access is returned from
      // the server with the dynamic content
      if (this.isDynamic && this.dynamicContentType) {
        this.fetchDynamicContent()
      }
    },

    collection() {
      clearTimeout(this.debounce)

      this.debounce = setTimeout(() => {
        if (this.isDynamic) {
          if (!this.dynamicContentType) {
            this.dynamicItems = []

            return
          }

          this.fetchDynamicContent()
        }
      }, 300)
    }
  },

  computed: {
    ...mapState("videos", { isVideoFullscreen: "isFullscreen" }),
    ...mapGetters("auth", ["isAuthenticated"]),

    isDynamic() {
      return this.collection.is_dynamic
    },

    dynamicContentType() {
      return this.collection.dynamic_content_type
    },

    fetchedItems() {
      return this.isDynamic ? this.dynamicItems : this.items
    },

    gridWidth() {
      const horizontalSpacing =
        (this.spaceXRatio * 0.01 - 0.5) * Math.max(0, this.itemColumns - 2)

      return `calc(${this.itemColumns *
        this.columnWidthRatio}% - ${horizontalSpacing}rem)`
    },

    gridMaxWidth() {
      return this.direction === HORIZONTAL
        ? `${this.itemColumns * HORIZONTAL_MAX_WIDTH}px`
        : null
    },

    cellWidth() {
      return withRatio(100, this.columnWidthRatio, { unit: "%" })
    },

    placeHolderNumber() {
      return this.itemColumns * this.itemRows
    },

    hasItems() {
      return this.fetchedItems.length > 0
    },

    collectionItemsPadding() {
      return toPadding(
        this.spaceTopRatio,
        this.spaceXRatio,
        this.spaceBottomRatio
      )
    },

    itemColumns() {
      if (!this.hasItems) {
        if (this.columns) {
          return this.columns
        } else if (this.rows) {
          return 1
        } else {
          return this.direction === HORIZONTAL ? 2 : 1
        }
      }

      if (this.columns && this.rows && this.direction === HORIZONTAL) {
        const r = this.rows
        return (
          this.columns +
          Math.ceil(
            Math.max(this.fetchedItems.length - r * this.columns, 0) / r
          )
        )
      }

      if (this.columns) {
        return this.columns
      } else if (this.rows) {
        return Math.ceil(this.fetchedItems.length / this.rows)
      } else {
        return this.direction === HORIZONTAL ? this.fetchedItems.length || 1 : 1
      }
    },

    itemRows() {
      if (!this.hasItems) {
        if (this.rows) {
          return this.rows
        } else {
          return 1
        }
      }

      if (this.rows && this.columns && this.direction === VERTICAL) {
        const c = this.columns
        return (
          this.rows +
          Math.ceil(Math.max(this.fetchedItems.length - c * this.rows, 0) / c)
        )
      }

      if (this.rows) {
        return this.rows
      } else if (this.columns) {
        return Math.ceil(this.fetchedItems.length / this.columns)
      } else {
        return this.direction === HORIZONTAL ? 1 : this.fetchedItems.length || 1
      }
    },

    flow() {
      return this.direction === HORIZONTAL ? "row" : "column"
    },

    hasOverflowX() {
      return !(
        (this.direction === VERTICAL && this.itemColumns === 1) ||
        this.fetchedItems.length === 1 ||
        (this.itemColumns === 2 && this.columnWidthRatio === 50)
      )
    }
  },

  methods: {
    ...mapActions("content", ["fetchItems"]),

    pluralize,
    isBuilderContext,

    fetchDynamicContent() {
      this.isLoading = true
      this.fetchItems({
        contentType: this.collection.dynamic_content_type,
        duration: this.collection.dynamic_duration_seconds,
        dynamicDirection: this.collection.dynamic_direction,
        includeCurrent: this.collection.dynamic_include_current,
        offset: this.collection.dynamic_offset,
        limit: this.collection.dynamic_limit,
        withTags: this.collection.tags.map(t => t.text),
        orderBy: this.collection.dynamic_order_by,
        withAllTags: this.collection.dynamic_with_all_tags
      })
        .then(items => {
          this.dynamicItems = items
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    showFreeBadge(item) {
      if (this.hidePrice) return false

      switch (item.type) {
        case "program":
          return item.data.is_free && item.data.show_is_free
        case "event_occurrence":
          return !item.data.is_paid
        default:
          return false
      }
    },

    itemKey(item) {
      return `${item.content_type}-${item.uuid || item.data.id}`
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.collection {
  overflow-x: hidden;
}

.collection__link {
  &.v-button.--small {
    margin-left: $size--2;
    margin-top: -$size--4;

    .v-icon {
      transform: scale(1.4);
      margin-left: $size--2;
    }
  }
}

.collection__content {
  @include container;

  &.--scroll-x {
    @include hide-scroll-bars;

    margin: initial;
    max-width: 100%;
    overflow-x: auto;
    padding-left: calc((100% - #{$content--max-width}) / 2);
  }
}

.collection__items {
  .collection__items-inner {
    column-gap: $size--4;
    row-gap: $size--4;
    display: grid;
  }

  .collection__items-cell {
    flex-shrink: 0;

    &.--placeholder {
      .collection-item {
        animation: none;
        opacity: 1;
      }
    }
  }

  &.--video-fullscreen {
    overflow: visible !important;
  }
}
</style>
